import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { FileUpload } from '../../models/fna';
import { GlobalsService } from '../../services/globals.service';
import { toWords } from 'number-to-words';

export interface FileObject {
  id?: string;
  name: string;
  size?: number | null;
  type?: string;
  data?: string;
}

export interface UploadedFile {
  file: FileObject;
  documentType?: string;
}

@Component({
  selector: 'app-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
})
export class FileUploadComponent implements OnInit {
  files: UploadedFile[] = [];
  INVALID_CHARACTERS = '~!;{}<>$%^=?"|';
  MAX_CHARACTERS = 80;

  @Input() form: FormGroup = this.formBuilder.group({
    uploadedFiles: this.formBuilder.array([], [Validators.required]),
  });

  @Input() maxFileSize = 5 * 1024 * 1024; // 5MB - the max file size for 1 file only
  @Input() maxTotalFileSize = 15 * 1024 * 1024; // the max file size for all files combined
  @Input() maxNoOfUpload = 1;
  @Input() showDocumentType = false;
  @Output() handleFileUpload = new EventEmitter<any>();
  @Output() handleFileDelete = new EventEmitter<any>();
  @Output() handleError = new EventEmitter<string>();

  constructor(
    private formBuilder: FormBuilder,
    public globals: GlobalsService,
  ) {}

  ngOnInit(): void {
    // console.log('[FileUploadComponent] ngOnInit');
    this.handleValueChange(this.uploadedFilesFormArray.value);

    this.uploadedFilesFormArray.valueChanges.subscribe((fileList) =>
      this.handleValueChange(fileList),
    );
  }

  handleValueChange(fileList: { file: FileUpload }[]) {
    // console.log('FILE LIST: ', fileList);
    this.files = fileList
      .filter((item) => item.file)
      .map((file: { file: FileUpload }) => {
        return {
          file: {
            id: file.file.fileId,
            name: file.file.name,
            size: file.file.size,
            type: file.file.type,
            data: file.file.data,
          },
        };
      });
  }

  /**
   * Trigger when file is drag-and-dropped.
   */
  async handleFileDropped($event: any) {
    await this.processUploadedFiles($event);
  }

  /**
   * Trigger when file is selected from browse.
   */
  async handleFileSelected($event: any) {
    await this.processUploadedFiles($event.target.files);
  }

  /**
   * Delete file from files list
   * @param index (File index)
   */
  deleteFile(index: number) {
    this.files.splice(index, 1);
    this.handleFileDelete.emit(index);
    this.uploadedFilesFormArray.removeAt(index);
  }

  /**
   * Simulate the upload process
   */
  uploadFilesSimulator(fileObj: UploadedFile) {
    // setTimeout(() => {
    //   if (index === this.files.length) {
    //     return;
    //   } else {
    //     // const progressInterval = setInterval(() => {
    //     //   if (this.files[index].progress === 100) {
    //     //     clearInterval(progressInterval);
    //     //     this.uploadFilesSimulator(index + 1);
    //     //   } else {
    //     //     this.files[index].progress += 5;
    //     //   }
    //     // }, 200);
    //   }
    // }, 1000);

    // console.log('[uploadFilesSimulator] File Length: ', this.files.length);
    this.handleFileUpload.emit(fileObj);
  }

  /**
   * Process uploaded files.
   * @param files (Files List)
   */
  async processUploadedFiles(files: Array<any>) {
    let totalNumberOfFiles = files.length;
    let totalFileSize = 0;

    if (totalNumberOfFiles > this.maxNoOfUpload) {
      this.handleError.emit(
        'You have exceeded the maximum file upload of ' +
          toWords(this.maxNoOfUpload) +
          ' files.',
      );
      return;
    }

    for (const file of files) {
      if (file.size > this.maxFileSize) {
        this.handleError.emit(
          'You have exceeded the total file size limit of ' +
            this.formatBytes(this.maxFileSize) +
            '.',
        );
        break;
      }

      totalFileSize += file.size;

      if (totalFileSize > this.maxTotalFileSize) {
        this.handleError.emit(
          'You have exceeded the total file size limit of ' +
            this.formatBytes(this.maxTotalFileSize),
        );
        break;
      }

      totalNumberOfFiles++;

      const fileToBase64 = (file: File): Promise<string> => {
        return new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => resolve(reader.result?.toString() || '');
          reader.onerror = (error) => reject(error);
        });
      };

      const fileBase64Data = await fileToBase64(file);

      // item.progress = 0;
      const fileObject = {
        file: {
          name: file.name,
          size: file.size,
          type: file.type,
          data: fileBase64Data,
        },
        documentType: '',
      };
      // this.files.push(fileObject);
      // this is use to handle file upload
      this.uploadFilesSimulator(fileObject);

      this.uploadedFilesFormArray.push(
        this.buildFileUploadedRowFormGroup(fileObject),
      );
    }
  }

  /**
   * format bytes
   * @param bytes (File size in bytes)
   * @param decimals (Decimals point)
   */
  formatBytes(bytes: number, decimals: number = 2) {
    if (bytes === 0) {
      return '0 Bytes';
    }
    const k = 1024;
    const dm = decimals <= 0 ? 0 : decimals || 2;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + sizes[i];
  }

  // Form control
  buildFileUploadedRowFormGroup(fileObject: UploadedFile) {
    const excludeCharactersRegex = '[^' + this.INVALID_CHARACTERS + ']+';
    return new FormGroup({
      file: new FormControl(fileObject.file, [Validators.required]),
      documentType: new FormControl(
        fileObject.documentType,
        this.showDocumentType
          ? [
              // Validators.required,
              // Validators.pattern(excludeCharactersRegex),
              // Validators.maxLength(this.MAX_CHARACTERS),
            ]
          : [],
      ),
    });
  }

  // region Form control getters
  get uploadedFilesFormArray() {
    return this.form.get('uploadedFiles') as FormArray;
  }
  get uploadedFilesFormArrayControls() {
    return (this.form.get('uploadedFiles') as FormArray).controls;
  }
  // endregion
}
